import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import clsx from "clsx";
import {
  closestCenter,
  defaultDropAnimation,
  DndContext,
  DragOverlay,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";

import { Button } from "../Buttons/Button";
import i18n from "../../i18n";
import { DashboardCardDefinition } from "./dashboardCards";
import { arrangeCards, CardSize, GRID_COLUMNS, moveCard } from "./dashboardLayout";
import { DashboardGridCard } from "./DashboardGridCard";
import { cardKeyboardCoordinates } from "./cardKeyboardCoordinates";
import { useDashboardLayout } from "./useDashboardLayout";
import { DashboardCardsMenu } from "./DashboardCardsMenu";
import { DashboardEditControls } from "./DashboardEditControls";

const DROP_ANIMATION: DropAnimation = { ...defaultDropAnimation, sideEffects: null };

const GRID_CLASS = "grid grid-cols-12 gap-6 xl:gap-8";

export const DashboardGrid: React.FC = () => {
  const {
    layout,
    editing,
    customisable,
    update,
    setHidden,
    startEditing,
    cancelEditing,
    finishEditing,
    resetToDefault,
  } = useDashboardLayout();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: cardKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!editing) return;

    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented && !draggedKey) cancelEditing();
    };
    window.addEventListener("keydown", cancelOnEscape);
    return () => window.removeEventListener("keydown", cancelOnEscape);
  }, [editing, draggedKey, cancelEditing]);

  const shown = layout.filter(placement => !placement.hidden);
  const arrangement = arrangeCards(shown, { editing });
  const dragged = shown.find(placement => placement.key === draggedKey);

  const hide = (card: DashboardCardDefinition) => {
    setHidden(card.key, true);
    enqueueSnackbar(i18n.t("frame.hidden", { ns: "dashboard", title: card.title }), {
      autoHideDuration: 6000,
      action: snackbarKey => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setHidden(card.key, false);
            closeSnackbar(snackbarKey);
          }}
        >
          {i18n.t("frame.undo", { ns: "dashboard" })}
        </Button>
      ),
    });
  };

  const resize = (key: string, size: Partial<CardSize>) =>
    update(latest => latest.map(placement => placement.key === key ? { ...placement, ...size } : placement));

  return (
    <div className="w-full">
      <div className="w-full flex justify-end items-center mb-3">
        {customisable && layout.length > 0 && (
          <DashboardEditControls
            editing={editing}
            onStart={startEditing}
            onCancel={cancelEditing}
            onFinish={finishEditing}
            onReset={resetToDefault}
          />
        )}
        <DashboardCardsMenu layout={layout} setHidden={setHidden} />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({ active }) => setDraggedKey(String(active.id))}
        onDragCancel={() => setDraggedKey(null)}
        onDragEnd={({ active, over }) => {
          setDraggedKey(null);
          if (over) update(latest => moveCard(latest, String(active.id), String(over.id)));
        }}
      >
        <MotionConfig reducedMotion="user">
          <div
            className={clsx(
              "relative isolate [--bleed:0px] xl:[--bleed:--spacing(2)] -m-(--bleed) p-(--bleed) rounded-xl transition-[outline-color]",
              editing ? "outline-1 outline-theme-text/30 bg-theme-focus/5" : "outline-transparent"
            )}
          >
            <AnimatePresence>
              {editing && (
                <motion.div
                  key="layout-guides"
                  data-testid="layout-guides"
                  aria-hidden
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className={clsx(GRID_CLASS, "absolute inset-0 -z-10 px-(--bleed) rounded-xl overflow-hidden pointer-events-none")}
                >
                  {Array.from({ length: GRID_COLUMNS }, (_, column) => (
                    <div key={column} className="first:-ml-(--bleed) last:-mr-(--bleed) bg-theme-focus/15 border border-dashed border-theme-focus/40" />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <div
              ref={gridRef}
              data-testid="dashboard-grid"
              className={clsx(GRID_CLASS, "w-full justify-items-center justify-center")}
            >
              <AnimatePresence initial={false}>
                {shown.map((placement, index) => (
                  <DashboardGridCard
                    key={placement.key}
                    placement={placement}
                    span={arrangement[index].span}
                    height={arrangement[index].height}
                    grid={gridRef}
                    editing={editing}
                    onHide={() => hide(placement.card)}
                    onResize={size => resize(placement.key, size)}
                  />
                ))}
              </AnimatePresence>
            </div>
            {editing && <div data-testid="layout-room" aria-hidden className="h-[250px]" />}
          </div>
        </MotionConfig>

        <DragOverlay dropAnimation={DROP_ANIMATION}>
          {dragged && (
            <div className="inline-block rounded-lg border border-theme-border bg-theme-surface shadow-xl shadow-theme-shadow px-5 py-4 text-2xl font-semibold text-theme-text cursor-grabbing">
              {dragged.card.title}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
